import React, { useCallback } from 'react';
import classNames from 'classnames';
import { Tree } from '@minoru/react-dnd-treeview';
import { convertFromFlatTree } from './helpers/convert-to-flat-tree';
import TreeViewItemContainer from './tree-view-item-container';
import TreeViewItemDragPreview from './components/tree-view-item-drag-preview';
import TreeViewItemPlaceholder from './components/tree-view-item-placeholder';
import noop from '../../../utils/noop';
import prepareResult from './helpers/prepare-result';
import { ROOT_ID } from './constants';
import useTreeViewData from './hooks/use-tree-view-data';

const treeClasses = {
  root: 'ws-ui-tree-view-list--root',
  container: 'ws-ui-tree-view-list',
  listItem: 'ws-ui-tree-view-item-container',
  draggingSource: 'ws-ui-tree-view-item-container--dragging',
  dropTarget: 'ws-ui-tree-view-item-container--drop-target',
  placeholder: 'ws-ui-tree-view-item-placeholder',
};

const TreeView = ({
  items,
  maxNestingLevel = 3,
  onChange = noop,
  onItemSelect = noop,
  onStartItemDrag = noop,
  onEndItemDrag = noop,
}) => {
  const { flatTree, isDragging, openNodeIds, itemsDepthMap, setIsDragging, setOpenNodeIds } =
    useTreeViewData(items);

  const handleDrop = useCallback(
    (newTree) => {
      const hierarchical = convertFromFlatTree(newTree, ROOT_ID);
      const orderBased = prepareResult(hierarchical);

      onChange(orderBased);
    },
    [onChange]
  );

  const handleChangeOpen = useCallback(
    (newOpenIds) => {
      setOpenNodeIds(newOpenIds);

      const updatedFlatTree = flatTree.map((node) => {
        const nodeData = node.data;
        if (!nodeData?.droppable) return node;

        return {
          ...node,
          data: {
            ...nodeData,
            state: {
              ...nodeData.state,
              isCollapsed: !newOpenIds.includes(nodeData.id),
            },
          },
        };
      });

      const hierarchical = convertFromFlatTree(updatedFlatTree, ROOT_ID);
      const orderBased = prepareResult(hierarchical);
      onChange(orderBased);
    },
    [setOpenNodeIds, flatTree, onChange]
  );

  const handleItemClick = useCallback(
    (item) => {
      if (!item.droppable && item.isSelectable && !item.state?.isSelected) {
        const updatedFlatTree = flatTree.map((node) => {
          const nodeData = node.data;
          if (!nodeData?.state) return node;
          if (nodeData.id !== item.id) return node;

          return {
            ...node,
            data: {
              ...nodeData,
              state: { ...nodeData.state, isSelected: nodeData.id === item.id },
            },
          };
        });

        const hierarchical = convertFromFlatTree(updatedFlatTree, ROOT_ID);
        const orderBased = prepareResult(hierarchical);
        onChange(orderBased);
      }

      onItemSelect(item);
    },
    [flatTree, onChange, onItemSelect]
  );

  const handleDragStart = useCallback(
    (node) => {
      setIsDragging(true);
      if (node.data) {
        onStartItemDrag(node.data);
      }
    },
    [onStartItemDrag, setIsDragging]
  );

  const handleDragEnd = useCallback(
    (node) => {
      setIsDragging(false);
      if (node.data) {
        onEndItemDrag(node.data);
      }
    },
    [onEndItemDrag, setIsDragging]
  );

  const canDrag = useCallback((node) => !node.data?.state?.isDisabled, []);

  const canDrop = useCallback(
    (_, options) => {
      const { dragSource, dropTarget, dragSourceId, dropTargetId } = options;

      if (!dragSource || dragSourceId === undefined) {
        return false;
      }

      if (dropTargetId === ROOT_ID || dropTargetId == null) {
        return true;
      }

      if (!dropTarget?.droppable || dropTargetId === dragSourceId) {
        return false;
      }

      const dragDepthEntry = itemsDepthMap.get(dragSourceId);
      const dropTargetEntry = itemsDepthMap.get(dropTargetId);

      if (!dragDepthEntry) return false;
      if (!dropTargetEntry) return false;

      const dropDepth = dropTargetEntry?.depth + 1;
      const dragMaxChildDepth = dragDepthEntry.maxChildDepth;
      const totalDepth = dropDepth + dragMaxChildDepth;

      return totalDepth <= maxNestingLevel;
    },
    [maxNestingLevel, itemsDepthMap]
  );

  const renderCallback = useCallback(
    (node, options) => (
      <TreeViewItemContainer
        {...options}
        node={node}
        itemsDepthMap={itemsDepthMap}
        onItemClick={handleItemClick}
      />
    ),
    [handleItemClick, itemsDepthMap]
  );

  const placeholderRenderCallback = useCallback(() => <TreeViewItemPlaceholder />, []);

  const dragPreviewRenderCallback = useCallback(
    (monitorProps) => <TreeViewItemDragPreview {...monitorProps} />,
    []
  );

  const classes = classNames('ws-ui-tree-view', {
    'ws-ui-tree-view--drag-mode': isDragging,
  });

  return (
    <div className={classes} data-drag-backend="multibackend">
      <Tree
        tree={flatTree}
        rootId={ROOT_ID}
        classes={treeClasses}
        initialOpen={openNodeIds}
        sort={false}
        insertDroppableFirst={false}
        dropTargetOffset={5}
        onDrop={handleDrop}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        canDrop={canDrop}
        canDrag={canDrag}
        onChangeOpen={handleChangeOpen}
        render={renderCallback}
        placeholderRender={placeholderRenderCallback}
        dragPreviewRender={dragPreviewRenderCallback}
      />
    </div>
  );
};

export default TreeView;
